<template>
  <section>
    <!-- email security -->
    <p
      v-dompurify-html="
        t('select-solutions-msp', {
          CustomerName: customerName,
        })
      "
      class="tw-mb-4"
    ></p>
    <section class="">
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="emailSecurity.switcher"
          name="emailSecurity-switcher"
          class="tw-mr-3 tw-mb-2"
          :class="[
            getConsumersCountForService(PHISH_SERVICE) === 0
              ? ''
              : 'tw-pointer-events-none tw-opacity-30',
          ]"
          :data-test-id="`edit-msp-email-security-toggle`"
          :value="v$.emailSecurity.$model.switcher"
          @update:value="
            (selected: boolean) => {
              form.emailSecurity.switcher = selected;

              if (isSEGCheckboxAvailable) {
                if (!selected) {
                  form.seg.switcher = false;
                }
              }
            }
          "
        />
        <span>
          {{ t("email-security-solution") }}
          <span
            v-if="getConsumersCountForService(PHISH_SERVICE) > 0"
            v-dompurify-html="enabledForCustomersLabel(PHISH_SERVICE)"
          />
        </span>
      </div>
      <div
        v-if="isSEGCheckboxAvailable"
        class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-ml-[56px] tw-mb-2"
      >
        <cl-input-checkbox
          id="seg-switcher"
          data-test-id="edit-customer-disable-email-security-checkbox"
          :checked="form.seg.switcher"
          :indeterminate="false"
          name="seg-switcher"
          :disabled="
            !form.emailSecurity.switcher ||
            !(getConsumersCountForService(SEG_SERVICE) === 0)
          "
          @change="form.seg.switcher = !form.seg.switcher"
        />
        <label for="seg-switcher" class="tw-italic">{{ t("seg-beta") }}</label>
        <span
          v-if="getConsumersCountForService(SEG_SERVICE) > 0"
          v-dompurify-html="enabledForCustomersLabel(SEG_SERVICE)"
        />
      </div>
      <div
        v-if="form.emailSecurity.switcher"
        class="tw-grid tw-grid-cols-2 tw-gap-5"
      >
        <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
          <cl-form-group>
            <cl-form-label :label-for="t('licenses')">
              {{ t("licenses") }}
            </cl-form-label>
            <cl-form-input
              :id="'seats_purchased'"
              v-model:value="v$.emailSecurity.seats_purchased.$model"
              :name="'seats_purchased'"
              :type="'number'"
              :placeholder="'Licenses'"
              :data-test-id="`edit-msp-email-security-licenses`"
              :state="
                !v$.emailSecurity.seats_purchased.$dirty
                  ? undefined
                  : !v$.emailSecurity.seats_purchased.$error
              "
              :min="0"
              :max="100000"
              @on-keydown="guardNumericInput"
              @on-paste="guardNumericPaste"
            />
            <span
              v-if="v$.emailSecurity.seats_purchased.$error"
              class="tw-text-system-error-500 tw-text-sm"
            >
              <span
                v-if="
                  !v$.emailSecurity.seats_purchased.maxValue.$response ||
                  !v$.emailSecurity.seats_purchased.minValue.$response ||
                  !v$.emailSecurity.seats_purchased.integer.$response ||
                  !v$.emailSecurity.seats_purchased.required.$response
                "
              >
                {{ t("licenses-purchased-validation") }}
              </span>
            </span>
          </cl-form-group>
        </div>
      </div>
    </section>

    <!-- phishing simulation -->
    <section class="tw-mb-5 tw-mt-3">
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="phishingSimulation-switcher"
          name="phishingSimulation-switcher"
          class="tw-mr-3"
          :class="[
            getConsumersCountForService(PHISHING_SIMULATION) === 0
              ? ''
              : 'tw-pointer-events-none tw-opacity-30',
          ]"
          :data-test-id="`edit-msp-phishing-simulation-toggle`"
          :value="v$.phishingSimulation.$model.switcher"
          @update:value="
            (selected: boolean) => (form.phishingSimulation.switcher = selected)
          "
        />
        <span>
          {{ t("phishing-simulation-label") }}
          <span
            v-if="getConsumersCountForService(PHISHING_SIMULATION) > 0"
            v-dompurify-html="enabledForCustomersLabel(PHISHING_SIMULATION)"
          />
        </span>
      </div>
      <div
        v-if="form.phishingSimulation.switcher"
        class="tw-grid tw-grid-cols-2 tw-gap-5"
      >
        <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
          <cl-form-group>
            <cl-form-label :label-for="t('licenses')">
              {{ t("licenses") }}
            </cl-form-label>
            <cl-form-input
              :id="'licenses'"
              v-model:value="v$.phishingSimulation.seats_purchased.$model"
              :name="'licenses'"
              :type="'number'"
              :placeholder="'Licenses'"
              :data-test-id="`edit-msp-phishing-simulation-licenses`"
              :state="
                !v$.phishingSimulation.seats_purchased.$dirty
                  ? undefined
                  : !v$.phishingSimulation.seats_purchased.$error
              "
              :min="0"
              :max="100000"
              @on-keydown="guardNumericInput"
              @on-paste="guardNumericPaste"
            />
            <span
              v-if="v$.phishingSimulation.seats_purchased.$error"
              class="tw-text-system-error-500 tw-text-sm"
            >
              <span
                v-if="
                  !v$.phishingSimulation.seats_purchased.maxValue.$response ||
                  !v$.phishingSimulation.seats_purchased.minValue.$response ||
                  !v$.phishingSimulation.seats_purchased.integer.$response ||
                  !v$.phishingSimulation.seats_purchased.required.$response
                "
              >
                {{ t("licenses-purchased-validation") }}
              </span>
            </span>
          </cl-form-group>
        </div>
      </div>
    </section>
    <!-- security awareness training -->
    <section class="tw-mb-5 tw-mt-3">
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="sat-switcher"
          name="sat-switcher"
          class="tw-mr-3"
          :class="[
            getConsumersCountForService(SAT_SERVICE) === 0
              ? ''
              : 'tw-pointer-events-none tw-opacity-30',
          ]"
          :data-test-id="`edit-msp-sat-toggle`"
          :value="v$.sat.$model.switcher"
          @update:value="(selected: boolean) => (form.sat.switcher = selected)"
        />
        <span>
          {{ t("sat-label") }}
          <span
            v-if="getConsumersCountForService(SAT_SERVICE) > 0"
            v-dompurify-html="enabledForCustomersLabel(SAT_SERVICE)"
          />
        </span>
      </div>
      <div v-if="form.sat.switcher" class="tw-grid tw-grid-cols-2 tw-gap-5">
        <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
          <cl-form-group>
            <cl-form-label :label-for="t('licenses')">
              {{ t("licenses") }}
            </cl-form-label>
            <cl-form-input
              :id="'licenses'"
              v-model:value="v$.sat.seats_purchased.$model"
              :name="'licenses'"
              :type="'number'"
              :placeholder="'Licenses'"
              :data-test-id="`edit-msp-sat-licenses`"
              :state="
                !v$.sat.seats_purchased.$dirty
                  ? undefined
                  : !v$.sat.seats_purchased.$error
              "
              :min="0"
              :max="100000"
              @on-keydown="guardNumericInput"
              @on-paste="guardNumericPaste"
            />
            <span
              v-if="v$.sat.seats_purchased.$error"
              class="tw-text-system-error-500 tw-text-sm"
            >
              <span
                v-if="
                  !v$.sat.seats_purchased.maxValue.$response ||
                  !v$.sat.seats_purchased.minValue.$response ||
                  !v$.sat.seats_purchased.integer.$response ||
                  !v$.sat.seats_purchased.required.$response
                "
              >
                {{ t("licenses-purchased-validation") }}
              </span>
            </span>
          </cl-form-group>
        </div>
      </div>
    </section>
    <!-- email archiving -->
    <section class="tw-mb-5 tw-mt-3">
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="emailArchiving-switcher"
          name="emailArchiving-switcher"
          class="tw-mr-3"
          :class="[
            getConsumersCountForService(ARC_SERVICE) === 0
              ? ''
              : 'tw-pointer-events-none tw-opacity-30',
          ]"
          :data-test-id="`edit-msp-email-archiving-toggle`"
          :value="v$.emailArchiving.$model.switcher"
          @update:value="
            (selected: boolean) => (form.emailArchiving.switcher = selected)
          "
        />
        <span>
          {{ t("email-archiving-label") }}
          <span
            v-if="getConsumersCountForService(ARC_SERVICE) > 0"
            v-dompurify-html="enabledForCustomersLabel(ARC_SERVICE)"
          />
        </span>
      </div>
      <div
        v-if="form.emailArchiving.switcher"
        class="tw-grid tw-grid-cols-2 tw-gap-5"
      >
        <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
          <cl-form-group>
            <cl-form-label :label-for="t('licenses')">
              {{ t("licenses") }}
            </cl-form-label>
            <cl-form-input
              :id="'licenses'"
              v-model:value="v$.emailArchiving.seats_purchased.$model"
              :name="'licenses'"
              :type="'number'"
              :placeholder="'Licenses'"
              :data-test-id="`edit-msp-email-archiving-licenses`"
              :state="
                !v$.emailArchiving.seats_purchased.$dirty
                  ? undefined
                  : !v$.emailArchiving.seats_purchased.$error
              "
              :min="0"
              :max="100000"
              @on-keydown="guardNumericInput"
              @on-paste="guardNumericPaste"
            />
            <span
              v-if="v$.emailArchiving.seats_purchased.$error"
              class="tw-text-system-error-500 tw-text-sm"
            >
              <span
                v-if="
                  !v$.emailArchiving.seats_purchased.maxValue.$response ||
                  !v$.emailArchiving.seats_purchased.minValue.$response ||
                  !v$.emailArchiving.seats_purchased.integer.$response ||
                  !v$.emailArchiving.seats_purchased.required.$response
                "
              >
                {{ t("licenses-purchased-validation") }}
              </span>
            </span>
          </cl-form-group>
        </div>
      </div>
    </section>
  </section>
</template>
<script lang="ts">
type Form = {
  emailSecurity: {
    seats_purchased: string | number;
    expires_at: string | undefined;
    maxioSubID: string;
    switcher: boolean;
  };
  emailArchiving: {
    seats_purchased: string | number;
    expires_at: string | undefined;
    maxioSubID: string;
    switcher: boolean;
  };
  phishingSimulation: {
    seats_purchased: string | number;
    expires_at: string | undefined;
    maxioSubID: string;
    switcher: boolean;
  };
  sat: {
    seats_purchased: string | number;
    expires_at: string | undefined;
    maxioSubID: string;
    switcher: boolean;
  };
  seg: {
    expires_at: string | undefined;
    maxioSubID: string;
    switcher: boolean;
  };
};

interface Props {
  form: Form;
  customerName: string;
  listAllLicences: ResellerOrganisationResponse;
  showTooltip: boolean;
  isCreateMspButtonDisabled: boolean;
}
</script>

<script setup lang="ts">
import { reactive, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { guardNumericInput, guardNumericPaste } from "@/utils/inputGuards";
import { useVuelidate } from "@vuelidate/core";
import {
  integer,
  maxValue,
  minValue,
  numeric,
  required,
} from "@vuelidate/validators";
import {
  LicenceWithSeats,
  ResellerOrganisationResponse,
} from "@/types/licenceSolutions.ts";
import {
  ARC_SERVICE,
  PHISH_SERVICE,
  PHISHING_SIMULATION,
  SAT_SERVICE,
  SEG_SERVICE,
} from "@/utils/services";
import { useIsSEGCheckboxAvailable } from "@/composables/useFlags/useIsSEGCheckboxAvailable";

const props = defineProps<Props>();
const form = reactive<Form>({ ...props.form });
const { isSEGCheckboxAvailable } = useIsSEGCheckboxAvailable();
const { t } = useI18n();

const emit = defineEmits<{
  (e: "update:form", form: Form): void;
  (e: "update:isCreateMspButtonDisabled", isValid: boolean): void;
  (e: "update:showTooltip", isValid: boolean): void;
}>();

const rules = {
  emailSecurity: {
    seats_purchased: {
      required,
      numeric,
      integer,
      maxValue: maxValue(100000),
      minValue: minValue(1),
    },
    maxioSubID: { required },
  },
  emailArchiving: {
    seats_purchased: {
      required,
      numeric,
      integer,
      maxValue: maxValue(100000),
      minValue: minValue(1),
    },
    maxioSubID: { required },
  },
  phishingSimulation: {
    seats_purchased: {
      required,
      numeric,
      integer,
      maxValue: maxValue(100000),
      minValue: minValue(1),
    },
    maxioSubID: { required },
  },
  sat: {
    seats_purchased: {
      required,
      numeric,
      integer,
      maxValue: maxValue(100000),
      minValue: minValue(1),
    },
    maxioSubID: { required },
  },
};
const v$ = useVuelidate(rules, form);
const getConsumersCountForService = (service_uuid: string) => {
  const licence = props.listAllLicences.licences.find(
    (obj: LicenceWithSeats) => obj.solution_uuid === service_uuid || false,
  );
  return licence ? licence.consumers_count : 0;
};

const enabledForCustomersLabel = (service_uuid: string) => {
  const count = getConsumersCountForService(service_uuid);
  return `(Enabled for <strong>${count}</strong> ${count === 1 ? "customer" : "customers"})`;
};

const isCreateMspButtonDisabled = computed(() => {
  return (
    (v$.value.emailSecurity.seats_purchased.$invalid &&
      form.emailSecurity.switcher) ||
    (v$.value.emailArchiving.seats_purchased.$invalid &&
      form.emailArchiving.switcher) ||
    (v$.value.phishingSimulation.seats_purchased.$invalid &&
      form.phishingSimulation.switcher) ||
    (v$.value.sat.seats_purchased.$invalid && form.sat.switcher)
  );
});

const showTooltip = computed(() => {
  return (
    (v$.value.emailSecurity.seats_purchased.$error &&
      form.emailSecurity.switcher) ||
    (v$.value.emailArchiving.seats_purchased.$error &&
      form.emailArchiving.switcher) ||
    (v$.value.phishingSimulation.seats_purchased.$error &&
      form.phishingSimulation.switcher)
  );
});

watch(isCreateMspButtonDisabled, (newValue) => {
  emit("update:isCreateMspButtonDisabled", newValue);
});

watch(form, (newValue) => {
  emit("update:form", newValue);
});
watch(showTooltip, (newValue) => {
  emit("update:showTooltip", newValue);
});
</script>
